import React, { useState, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  ProgressBar,
  Badge,
  Modal,
} from "react-bootstrap";
// Ensure you have bootstrap CSS imported in your app (see README below)
// npm install react-bootstrap bootstrap html2pdf.js

export default function ManualResumeBuilder() {
  const [step, setStep] = useState(1);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const resumeRef = useRef(null);

  const [form, setForm] = useState({
    // Step 1: Personal
    fullName: "",
    email: "",
    phone: "",
    website: "",
    linkedin: "",
    github: "",
    location: "",
    // Step 2: Summary & Skills
    summary: "",
    skills: "",
    // Step 3: Experience (as JSON string or structured fields)
    experiences: [
      {
        title: "",
        company: "",
        start: "",
        end: "",
        location: "",
        details: "",
      },
    ],
    // Step 4: Projects & Education
    projects: [
      { name: "", desc: "", tech: "" },
    ],
    education: [
      { degree: "", institute: "", years: "", score: "" },
    ],
    // Step 5: Certifications & Extras
    certifications: "",
    languages: "",
  });

  // helpers to update arrays
  function updateArrayField(path, index, key, value) {
    const arr = [...form[path]];
    arr[index] = { ...arr[index], [key]: value };
    setForm({ ...form, [path]: arr });
  }

  function addArrayItem(path, template) {
    setForm({ ...form, [path]: [...form[path], template] });
  }

  function removeArrayItem(path, idx) {
    const arr = [...form[path]];
    arr.splice(idx, 1);
    setForm({ ...form, [path]: arr });
  }

  function nextStep() {
    if (step < 5) setStep(step + 1);
  }
  function prevStep() {
    if (step > 1) setStep(step - 1);
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  }

  // Minimal validation per step
  function canProceed() {
    if (step === 1) return form.fullName.trim() && form.email.trim();
    if (step === 2) return form.summary.trim();
    return true;
  }

  // Generate PDF using html2pdf
  async function generatePDF() {
    const html2pdf = (await import("html2pdf.js"));
    const element = resumeRef.current;
    const opt = {
      margin: 10,
      filename: `${form.fullName ? form.fullName.replace(/\s+/g, "_") : "resume"}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
    };
    html2pdf().set(opt).from(element).save();
  }

  // Render resume preview (simple, printable layout inspired by provided sample)
  function ResumePreview() {
    return (
      <div ref={resumeRef} style={{ padding: 24, background: "#fff", color: "#111" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h1 style={{ margin: 0 }}>{form.fullName || "Your Name"}</h1>
            <div style={{ marginTop: 6 }}>{form.summary || "Professional summary goes here..."}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div>{form.email}</div>
            <div>{form.phone}</div>
            <div>{form.location}</div>
            <div>
              {form.linkedin && (
                <a href={form.linkedin} target="_blank" rel="noreferrer">
                  linkedin
                </a>
              )}{" "}
              {form.github && (
                <a href={form.github} target="_blank" rel="noreferrer">
                  github
                </a>
              )}
            </div>
          </div>
        </div>

        <hr />

        <h5>Skills</h5>
        <div style={{ marginBottom: 12 }}>
          {(form.skills || "").split(",").map((s, i) => (
            s.trim() && (
              <Badge key={i} pill bg="secondary" style={{ marginRight: 6 }}>
                {s.trim()}
              </Badge>
            )
          ))}
        </div>

        <h5>Experience</h5>
        {form.experiences.map((exp, idx) => (
          <div key={idx} style={{ marginBottom: 10 }}>
            <strong>{exp.title || "Title"}</strong> — {exp.company} <br />
            <small>
              {exp.start} — {exp.end} · {exp.location}
            </small>
            <div style={{ marginTop: 6 }}>{exp.details}</div>
          </div>
        ))}

        <h5>Projects</h5>
        {form.projects.map((p, i) => (
          <div key={i} style={{ marginBottom: 8 }}>
            <strong>{p.name}</strong>
            <div>{p.desc}</div>
            <small>Tech: {p.tech}</small>
          </div>
        ))}

        <h5>Education</h5>
        {form.education.map((e, i) => (
          <div key={i} style={{ marginBottom: 6 }}>
            <strong>{e.degree}</strong> — {e.institute}
            <div>{e.years} · {e.score}</div>
          </div>
        ))}

        <h5>Certifications</h5>
        <div>{form.certifications}</div>

        <div style={{ marginTop: 20, fontSize: 11, color: "#666" }}>
          Generated with Olewin Academy Resume Builder
        </div>
      </div>
    );
  }

  return (
    <Container className="my-5 py-5 " fluid style={{ padding: 20 }}>
      <Row>
        <Col md={7}>
          <h3>Resume Builder (Step {step} of 5)</h3>
          <ProgressBar now={(step / 5) * 100} className="mb-3" />

          {/* Step Forms */}
          {step === 1 && (
            <Form>
              <Form.Group className="mb-2">
                <Form.Label>Full Name</Form.Label>
                <Form.Control name="fullName" value={form.fullName} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Email</Form.Label>
                <Form.Control name="email" value={form.email} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Phone</Form.Label>
                <Form.Control name="phone" value={form.phone} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Location</Form.Label>
                <Form.Control name="location" value={form.location} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Website / Portfolio</Form.Label>
                <Form.Control name="website" value={form.website} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>LinkedIn</Form.Label>
                <Form.Control name="linkedin" value={form.linkedin} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>GitHub</Form.Label>
                <Form.Control name="github" value={form.github} onChange={handleChange} />
              </Form.Group>
            </Form>
          )}

          {step === 2 && (
            <Form>
              <Form.Group className="mb-2">
                <Form.Label>Professional Summary</Form.Label>
                <Form.Control as="textarea" rows={4} name="summary" value={form.summary} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Skills (comma separated)</Form.Label>
                <Form.Control name="skills" value={form.skills} onChange={handleChange} />
              </Form.Group>
            </Form>
          )}

          {step === 3 && (
            <div>
              {form.experiences.map((exp, idx) => (
                <div key={idx} className="p-2 mb-2" style={{ border: "1px solid #eee" }}>
                  <Row>
                    <Col>
                      <Form.Group>
                        <Form.Label>Job Title</Form.Label>
                        <Form.Control value={exp.title} onChange={(e) => updateArrayField("experiences", idx, "title", e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group>
                        <Form.Label>Company</Form.Label>
                        <Form.Control value={exp.company} onChange={(e) => updateArrayField("experiences", idx, "company", e.target.value)} />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mt-2">
                    <Col>
                      <Form.Group>
                        <Form.Label>Start</Form.Label>
                        <Form.Control value={exp.start} onChange={(e) => updateArrayField("experiences", idx, "start", e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group>
                        <Form.Label>End</Form.Label>
                        <Form.Control value={exp.end} onChange={(e) => updateArrayField("experiences", idx, "end", e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group>
                        <Form.Label>Location</Form.Label>
                        <Form.Control value={exp.location} onChange={(e) => updateArrayField("experiences", idx, "location", e.target.value)} />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mt-2">
                    <Form.Label>Details / Achievements</Form.Label>
                    <Form.Control as="textarea" rows={3} value={exp.details} onChange={(e) => updateArrayField("experiences", idx, "details", e.target.value)} />
                  </Form.Group>
                  <div className="mt-2 d-flex justify-content-end">
                    <Button variant="outline-danger" size="sm" onClick={() => removeArrayItem("experiences", idx)}>Remove</Button>
                  </div>
                </div>
              ))}

              <Button onClick={() => addArrayItem("experiences", { title: "", company: "", start: "", end: "", location: "", details: "" })}>Add Experience</Button>
            </div>
          )}

          {step === 4 && (
            <div>
              <h6>Projects</h6>
              {form.projects.map((p, i) => (
                <div key={i} className="p-2 mb-2" style={{ border: "1px solid #eee" }}>
                  <Form.Group>
                    <Form.Label>Project Name</Form.Label>
                    <Form.Control value={p.name} onChange={(e) => updateArrayField("projects", i, "name", e.target.value)} />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control as="textarea" rows={2} value={p.desc} onChange={(e) => updateArrayField("projects", i, "desc", e.target.value)} />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Technologies (comma separated)</Form.Label>
                    <Form.Control value={p.tech} onChange={(e) => updateArrayField("projects", i, "tech", e.target.value)} />
                  </Form.Group>
                  <div className="d-flex justify-content-end mt-2">
                    <Button variant="outline-danger" size="sm" onClick={() => removeArrayItem("projects", i)}>Remove</Button>
                  </div>
                </div>
              ))}

              <Button onClick={() => addArrayItem("projects", { name: "", desc: "", tech: "" })}>Add Project</Button>

              <hr />

              <h6>Education</h6>
              {form.education.map((e, i) => (
                <div key={i} className="p-2 mb-2" style={{ border: "1px solid #eee" }}>
                  <Form.Group>
                    <Form.Label>Degree</Form.Label>
                    <Form.Control value={e.degree} onChange={(ev) => updateArrayField("education", i, "degree", ev.target.value)} />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Institute</Form.Label>
                    <Form.Control value={e.institute} onChange={(ev) => updateArrayField("education", i, "institute", ev.target.value)} />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Years</Form.Label>
                    <Form.Control value={e.years} onChange={(ev) => updateArrayField("education", i, "years", ev.target.value)} />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Score / GPA</Form.Label>
                    <Form.Control value={e.score} onChange={(ev) => updateArrayField("education", i, "score", ev.target.value)} />
                  </Form.Group>
                  <div className="d-flex justify-content-end mt-2">
                    <Button variant="outline-danger" size="sm" onClick={() => removeArrayItem("education", i)}>Remove</Button>
                  </div>
                </div>
              ))}

              <Button onClick={() => addArrayItem("education", { degree: "", institute: "", years: "", score: "" })}>Add Education</Button>
            </div>
          )}

          {step === 5 && (
            <div>
              <Form.Group className="mb-2">
                <Form.Label>Certifications (comma separated)</Form.Label>
                <Form.Control name="certifications" value={form.certifications} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Languages</Form.Label>
                <Form.Control name="languages" value={form.languages} onChange={handleChange} />
              </Form.Group>

              <div className="d-flex gap-2 mt-3">
                <Button onClick={() => setShowPreviewModal(true)}>Preview</Button>
                <Button onClick={generatePDF} variant="primary">Download PDF</Button>
              </div>
            </div>
          )}

          <div className="d-flex justify-content-between mt-3">
            <div>
              <Button onClick={prevStep} disabled={step === 1} variant="light">Back</Button>
            </div>
            <div>
              {step < 5 ? (
                <Button onClick={nextStep} disabled={!canProceed()}>Next</Button>
              ) : (
                <Button onClick={() => { setShowPreviewModal(true); }}>Finish</Button>
              )}
            </div>
          </div>
        </Col>

        <Col md={5} style={{ maxHeight: "85vh", overflow: "auto", background: "#f8f9fa", padding: 16 }}>
          <h5 className="mb-3">Live Preview</h5>
          <div style={{ border: "1px solid #ddd", padding: 12, background: "#fff" }}>
            <ResumePreview />
          </div>
        </Col>
      </Row>

      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Resume Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ResumePreview />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>Close</Button>
          <Button onClick={generatePDF} variant="primary">Download PDF</Button>
        </Modal.Footer>
      </Modal>

      <hr />
      <div style={{ fontSize: 12, color: "#666" }}>
        Tips: Fill all fields carefully. Use commas to separate skills & technologies. Click Download PDF to get an A4 resume. For more precise styling to match your provided sample, tweak the ResumePreview JSX.
      </div>
    </Container>
  );
}
